import { Chapter } from '../../types/curriculum';
import React, { useState, useEffect, useMemo } from 'react';
import {
  ExternalLink,
  Hash,
  BookOpen,
  Layers,
  Clock,
  GitBranch,
  HelpCircle,
  Box,
  Image,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Tag,
  Edit,
  Eye,
  Play,
  MoreVertical,
  Calendar,
  XCircle,
  Loader2,
  Presentation,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { updateTopicApproval, updateTopicDemoFlag } from '../../lib/firestore/updateHelpers';
import { approveChapter, unapproveChapter } from '../../lib/firebase/queries/curriculumChapters';
// The same judgement the players make about whether an asset will load, so the
// badge and the scene cannot disagree.
import {
  assetBadgeState,
  fetchAssetHealth,
  type AssetHealthMap,
} from '../../lib/studio/assetHealth';
import { groupChapters } from '../../lib/studio/groupChapters';
import { isAdminOnly, isSuperadmin } from '../../utils/rbac';
import { toast } from 'react-hot-toast';
import { Button } from '../ui/button';

/**
 * Fields of the chapter document this table reads that the `Chapter` type does
 * not declare. The surrounding code reads them untyped; naming them here keeps
 * the new asset-health path honest without touching the shared type, which many
 * other screens depend on.
 */
type ChapterAssetFields = {
  meshy_asset_ids?: string[];
  topics?: Array<{ meshy_asset_ids?: string[] }>;
  image3dasset?: { imageasset_url?: string };
};

interface ChapterTableProps {
  chapters: Chapter[];
  onOpenChapter: (
    chapter: Chapter,
    chapterGroup?: {
      chapterIds: string[];
      groupKey: string;
    }
  ) => void;
  loading?: boolean;
  onApprovalChange?: () => void; // Callback to refresh data after approval change
}

interface GroupedChapter {
  curriculum: string;
  class: number;
  subject: string;
  chapterNumber: number;
  chapterName: string;
  topics: Array<{
    chapter: Chapter;
    topic: any;
    topicPriority: number;
  }>;
}

export const ChapterTable = ({
  chapters,
  onOpenChapter,
  loading,
  onApprovalChange,
}: ChapterTableProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const { profile } = useAuth();
  const [updatingApproval, setUpdatingApproval] = useState<string | null>(null);
  const [updatingChapterApproval, setUpdatingChapterApproval] = useState<string | null>(null);
  const [updatingDemoFlag, setUpdatingDemoFlag] = useState<string | null>(null);
  
  // Debug: Log approval permissions
  useEffect(() => {
    if (profile) {
      const canApproveCheck = isAdminOnly(profile) || isSuperadmin(profile);
      console.log('🔐 Approval permissions check:', {
        userRole: profile.role,
        isAdminOnly: isAdminOnly(profile),
        isSuperadmin: isSuperadmin(profile),
        canApprove: canApproveCheck,
      });
    }
  }, [profile]);
  
  const formatDate = (dateInput?: string | { toDate?: () => Date } | null) => {
    if (dateInput == null) return '—';
    try {
      const date = typeof dateInput === 'object' && typeof dateInput.toDate === 'function'
        ? dateInput.toDate()
        : new Date(dateInput as string);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };
  
  /*
    Grouping lives in lib/studio/groupChapters so it can be tested.

    It concatenated the topic arrays of every document sharing a logical
    chapter, and a chapter held as more than one document — usually one per
    language — carries the same topics in each. Every topic was therefore listed
    once per document, and the count said ten where there were five.
  */
  const groupedChapters = (): GroupedChapter[] =>
    groupChapters(chapters) as unknown as GroupedChapter[];

  const toggleGroup = (key: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedGroups(newExpanded);
  };
  
  // Check if user can approve (admin or superadmin)
  const canApprove = profile && (isAdminOnly(profile) || isSuperadmin(profile));
  
  // Handle chapter approval toggle
  const handleChapterApprovalToggle = async (chapterId: string, currentApproved: boolean) => {
    if (!canApprove || !profile) return;
    
    setUpdatingChapterApproval(chapterId);
    
    try {
      if (currentApproved) {
        await unapproveChapter(chapterId);
        toast.success('Chapter unapproved successfully');
      } else {
        await approveChapter(chapterId, profile.uid);
        toast.success('Chapter approved successfully');
      }
      
      // Trigger a refresh by calling the callback if provided
      if (onApprovalChange) {
        onApprovalChange();
      } else {
        // Fallback: reload page if no callback provided
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating chapter approval:', error);
      toast.error('Failed to update approval status');
    } finally {
      setUpdatingChapterApproval(null);
    }
  };
  
  // Handle topic approval toggle
  const handleApprovalToggle = async (chapterId: string, topicId: string, currentApproved: boolean) => {
    if (!canApprove || !profile) return;
    
    const approvalKey = `${chapterId}_${topicId}`;
    setUpdatingApproval(approvalKey);
    
    try {
      await updateTopicApproval({
        chapterId,
        topicId,
        approved: !currentApproved,
        userId: profile.uid,
      });
      
      toast.success(`Topic ${!currentApproved ? 'approved' : 'unapproved'} successfully`);
      
      // Trigger a refresh by calling the callback if provided
      if (onApprovalChange) {
        onApprovalChange();
      } else {
        // Fallback: reload page if no callback provided
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating topic approval:', error);
      toast.error('Failed to update approval status');
    } finally {
      setUpdatingApproval(null);
    }
  };

  const handleDemoToggle = async (chapterId: string, topicId: string, currentIsDemo: boolean) => {
    if (!canApprove || !profile) return;

    const demoKey = `${chapterId}_${topicId}`;
    setUpdatingDemoFlag(demoKey);

    try {
      await updateTopicDemoFlag({
        chapterId,
        topicId,
        isDemo: !currentIsDemo,
        userId: profile.uid,
      });

      toast.success(`Topic ${!currentIsDemo ? 'marked as demo' : 'removed from demo'} successfully`);

      if (onApprovalChange) {
        onApprovalChange();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating topic demo flag:', error);
      toast.error('Failed to update demo status');
    } finally {
      setUpdatingDemoFlag(null);
    }
  };

  // Format approvedAt timestamp
  const formatApprovedAt = (timestamp: any) => {
    if (!timestamp) return null;
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return null;
    }
  };
  
  // Helper to check if chapter has content
  const getContentStatus = (chapter: Chapter) => {
    const hasMCQs = (chapter.mcq_ids && chapter.mcq_ids.length > 0) || 
                   (chapter.topics?.some(t => t.mcq_ids && t.mcq_ids.length > 0));
    const has3DAssets = (chapter.meshy_asset_ids && chapter.meshy_asset_ids.length > 0) ||
                        (chapter.image3dasset?.imageasset_url) ||
                        (chapter.topics?.some(t => t.meshy_asset_ids && t.meshy_asset_ids.length > 0));
    const hasImages = (chapter.image_ids && chapter.image_ids.length > 0);
    
    return { hasMCQs, has3DAssets, hasImages };
  };
  
  const groups = groupedChapters();

  /*
    Which linked 3D assets can actually be loaded.

    The badge used to light for the mere existence of an asset id, which said
    nothing about whether the asset still resolved — and since Meshy's newer
    release most of the older ones answer 502. A chapter of dead models looked
    fully populated, with no way to see what needed regenerating.

    One batched read per 30 assets across the chapters on screen, and the list is
    paginated, so this is bounded. The classification is structural — the shape
    of the URL — not a request to it.
  */
  const [assetHealth, setAssetHealth] = useState<AssetHealthMap>(new Map());

  const linkedAssetIds = useMemo(() => {
    const ids = new Set<string>();
    for (const raw of chapters) {
      const chapter = raw as typeof raw & ChapterAssetFields;
      for (const id of chapter.meshy_asset_ids || []) if (id) ids.add(String(id));
      for (const topic of chapter.topics || []) {
        for (const id of topic.meshy_asset_ids || []) if (id) ids.add(String(id));
      }
    }
    return Array.from(ids).sort();
  }, [chapters]);

  // Keyed on the ids themselves: re-fetching because the array identity changed
  // would re-read on every render of the same content.
  const assetIdKey = linkedAssetIds.join(',');

  useEffect(() => {
    if (!assetIdKey) {
      setAssetHealth(new Map());
      return;
    }
    let cancelled = false;
    void fetchAssetHealth(assetIdKey.split(',')).then((health) => {
      if (!cancelled) setAssetHealth(health);
    });
    return () => {
      cancelled = true;
    };
  }, [assetIdKey]);

  /*
    One row, two layouts.

    Below lg the seven columns cannot fit any screen honestly, and forcing them
    into a 1300px grid meant the whole table scrolled sideways — the actions were
    off the right edge on every laptop narrower than that. Below lg the cells
    flow and wrap instead, with the actions pushed to the end of the line; from
    lg up it is the aligned seven-column table the header describes.

    The column widths are what the tracks actually add up to: 56+200+110+140+80+
    110+460, plus six gaps and the horizontal padding, so the minimum is only
    applied where the grid is.
  */
  const gridColumns = 'lg:grid-cols-[56px_minmax(200px,1fr)_110px_140px_80px_110px_minmax(420px,auto)]';
  const tableGrid =
    `flex flex-wrap items-center gap-x-3 gap-y-2 px-4 sm:px-6 w-full ` +
    `lg:grid ${gridColumns} lg:gap-4 lg:min-w-[1260px]`;

  return (
    <div className="bg-card rounded-xl border border-border relative overflow-hidden flex flex-col">
      <div className="w-full overflow-x-auto pb-4">
        {/* Table Header - aligned columns, clear hierarchy */}
        <header
          className={`hidden lg:grid ${gridColumns} lg:gap-4 px-4 sm:px-6 w-full lg:min-w-[1260px] py-3 bg-muted/50 border-b border-border items-center min-h-[44px]`}
        >
          <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <Hash className="w-3.5 h-3.5 text-primary shrink-0" />
            <span>#</span>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <BookOpen className="w-3.5 h-3.5 text-primary shrink-0" />
            <span>Topic Name</span>
          </div>
          <div className="flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Status</span>
          </div>
          <div className="flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Content</span>
          </div>
          <div className="flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Version</span>
          </div>
          <div className="flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Updated</span>
          </div>
          <div className="flex items-center justify-end text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Actions</span>
          </div>
        </header>
        
        {/* Table Body - Grouped by Chapter */}
        <div className="divide-y divide-border">
        {groups.map((group) => {
          const groupKey = `${group.curriculum}_${group.class}_${group.subject}_${group.chapterNumber}`;
          const isExpanded = expandedGroups.has(groupKey);
          
          return (
            <div key={groupKey} className="bg-card/30">
              {/* Chapter row - same grid as header for column alignment */}
              <div className={`${tableGrid} py-3.5 items-center min-h-[52px] hover:bg-muted/30 transition-colors border-l-4 border-primary/50 bg-card group`}>
                <div className="flex items-center justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-primary hover:bg-primary/10 shrink-0"
                    onClick={() => toggleGroup(groupKey)}
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-primary/10 border border-border shrink-0">
                    <BookOpen className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-foreground truncate">{group.chapterName}</span>
                      <span className="text-[10px] font-medium text-muted-foreground px-2 py-0.5 rounded border border-border shrink-0">Ch. {group.chapterNumber}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      <span className="text-[10px] font-semibold text-primary uppercase px-2 py-0.5 bg-primary/10 rounded border border-primary/20">{group.curriculum}</span>
                      <span className="text-muted-foreground text-[10px]">•</span>
                      <span className="text-[10px] text-muted-foreground">Class {group.class}</span>
                      <span className="text-muted-foreground text-[10px]">•</span>
                      <span className="text-[10px] text-muted-foreground truncate">{group.subject}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-foreground bg-muted rounded-md border border-border">
                    {group.topics.length} {group.topics.length === 1 ? 'topic' : 'topics'}
                  </span>
                </div>
                {/* Content: badges describe a topic, and a chapter covers several. */}
                <div className="flex items-center" aria-hidden />
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium text-muted-foreground bg-muted rounded border border-border">
                    {group.topics[0]?.chapter.current_version || 'v1'}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[11px] text-muted-foreground">
                    {formatDate(group.topics[0]?.chapter.updated_at)}
                  </span>
                </div>
                <div className="flex items-center justify-end gap-2 ml-auto lg:ml-0">
                  {canApprove && group.topics.length > 0 && (() => {
                    const firstChapter = group.topics[0].chapter;
                    const isApproved = (firstChapter as any).approved === true;
                    const isUpdating = updatingChapterApproval === firstChapter.id;
                    return (
                      <Button
                        type="button"
                        size="sm"
                        variant={isApproved ? 'destructive' : 'default'}
                        className="gap-1.5 text-xs h-8 shrink-0 whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChapterApprovalToggle(firstChapter.id, isApproved);
                        }}
                        disabled={isUpdating}
                      >
                        {isUpdating ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin" />{isApproved ? 'Unapproving...' : 'Approving...'}</>
                        ) : isApproved ? (
                          <><XCircle className="w-3.5 h-3.5" />Unapprove</>
                        ) : (
                          <><CheckCircle2 className="w-3.5 h-3.5" />Approve</>
                        )}
                      </Button>
                    );
                  })()}
                  {group.topics.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      className="gap-1.5 text-xs h-8 shrink-0 whitespace-nowrap"
                      onClick={(e) => {
                        e.stopPropagation();
                        const firstTopic = group.topics[0];
                        if (firstTopic) {
                          onOpenChapter(firstTopic.chapter, {
                            chapterIds: Array.from(new Set(group.topics.map(({ chapter }) => chapter.id))),
                            groupKey,
                          });
                        }
                      }}
                    >
                      <Play className="w-3.5 h-3.5" />
                      Open
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Topics List (when expanded) */}
              {isExpanded && (
                <div className="divide-y divide-border bg-muted/10">
                  {group.topics.map(({ chapter, topic, topicPriority }, index) => {
                    const contentStatus = getContentStatus(chapter);
                    const displayNum = topic?.topic_priority ?? index + 1;
                    const topicName = (topic?.topic_name && topic.topic_name.trim())
                      ? topic.topic_name
                      : `Topic ${displayNum}`;
                    const approval = topic?.approval || {};
                    const isApproved =
                      approval?.approved === true ||
                      approval?.approved === 'true' ||
                      (topic as { approved?: boolean })?.approved === true ||
                      (!topic?.approval && (chapter as { approved?: boolean }).approved === true);

                    return (
                      <div
                        key={`${chapter.id}_${topic?.topic_id ?? index}`}
                        className={`${tableGrid} py-3 items-center min-h-[52px] pl-12 sm:pl-14 hover:bg-muted/20 transition-colors border-l-2 border-transparent hover:border-primary/40`}
                      >
                        <div className="flex items-center justify-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold text-primary bg-primary/10 rounded-md border border-border">
                            {displayNum}
                          </span>
                        </div>
                        <div className="flex items-center min-w-0">
                          <div className="min-w-0">
                            <span className="text-sm font-semibold text-foreground truncate block">{topicName}</span>
                            {topic?.learning_objective && (
                              <p className="text-[11px] text-muted-foreground truncate mt-0.5" title={typeof topic.learning_objective === 'string' ? topic.learning_objective : (topic.learning_objective?.en || topic.learning_objective?.hi || '')}>
                                {typeof topic.learning_objective === 'string' ? topic.learning_objective : (topic.learning_objective?.en || topic.learning_objective?.hi || '')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {isApproved ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-primary bg-primary/10 rounded-md border border-primary/20">
                              <CheckCircle2 className="w-3 h-3 shrink-0" />
                              Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-amber-600 bg-amber-500/10 rounded-md border border-amber-500/30 dark:text-amber-400">
                              <AlertCircle className="w-3 h-3 shrink-0" />
                              Not Approved
                            </span>
                          )}
                          {(topic as { isDemo?: boolean }).isDemo === true && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-cyan-600 bg-cyan-500/10 rounded-md border border-cyan-500/30 dark:text-cyan-400" title="Shown to guest students on login">
                              <Presentation className="w-3 h-3 shrink-0" />
                              Demo
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${contentStatus.hasMCQs ? 'text-primary bg-primary/10 border border-primary/20' : 'text-muted-foreground bg-muted border border-border'}`} title="MCQ">MCQ</span>
                          {(() => {
                            /*
                              Three states, because "no models" and "models that
                              all died" are different problems and only one of
                              them needs someone to act. They used to look
                              identical, both simply lit, which is how a chapter
                              of unloadable assets passed for finished content.
                            */
                            const state = assetBadgeState(
                              (topic as { meshy_asset_ids?: string[] }).meshy_asset_ids || [],
                              assetHealth,
                              // Inline image-to-3D models live on the chapter, not
                              // the topic, and have no asset document to classify;
                              // their presence is all there is to go on.
                              (chapter as typeof chapter & ChapterAssetFields).image3dasset
                                ?.imageasset_url
                            );
                            const style =
                              state === 'ok'
                                ? 'text-primary bg-primary/10 border border-primary/20'
                                : state === 'broken'
                                  ? 'text-amber-600 bg-amber-500/10 border border-amber-500/30 dark:text-amber-400'
                                  : 'text-muted-foreground bg-muted border border-border';
                            const title =
                              state === 'ok'
                                ? '3D models ready'
                                : state === 'broken'
                                  ? '3D models are linked but none of them can be loaded — this topic needs regenerating'
                                  : 'No 3D models';
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${style}`}
                                title={title}
                              >
                                3D
                              </span>
                            );
                          })()}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${contentStatus.hasImages ? 'text-primary bg-primary/10 border border-primary/20' : 'text-muted-foreground bg-muted border border-border'}`} title="IMG">IMG</span>
                        </div>
                        <div className="flex items-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-muted-foreground bg-muted rounded border border-border">
                            {chapter.current_version || 'v1'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-[11px] text-muted-foreground" title={formatDate(chapter.updated_at)}>
                            {formatDate(chapter.updated_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-end gap-2 ml-auto lg:ml-0">
                          {(() => {
                            const approvalInner = topic?.approval || {};
                            const isApprovedInner =
                              approvalInner?.approved === true ||
                              approvalInner?.approved === 'true' ||
                              (topic as { approved?: boolean })?.approved === true;
                            const approvalKey = `${chapter.id}_${topic?.topic_id ?? ''}`;
                            const isUpdating = updatingApproval === approvalKey;
                            return (
                              <>
                                {canApprove && topic?.topic_id && (
                                  <>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant={isApprovedInner ? 'destructive' : 'default'}
                                      className="gap-1.5 text-xs h-8 shrink-0 whitespace-nowrap"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleApprovalToggle(chapter.id, topic.topic_id, isApprovedInner);
                                      }}
                                      disabled={isUpdating}
                                      title={isApprovedInner ? 'Unapprove topic' : 'Approve topic'}
                                    >
                                      {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isApprovedInner ? <><XCircle className="w-3.5 h-3.5" />Unapprove</> : <><CheckCircle2 className="w-3.5 h-3.5" />Approve</>}
                                    </Button>
                                    {(() => {
                                      const isDemo = (topic as { isDemo?: boolean }).isDemo === true;
                                      const demoKey = `${chapter.id}_${topic?.topic_id ?? ''}`;
                                      const isUpdatingDemo = updatingDemoFlag === demoKey;
                                      return (
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant={isDemo ? 'default' : 'outline'}
                                          className="gap-1.5 text-xs h-8 shrink-0 whitespace-nowrap"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDemoToggle(chapter.id, topic.topic_id, isDemo);
                                          }}
                                          disabled={isUpdatingDemo}
                                          title={isDemo ? 'Remove from demo (guest students)' : 'Mark as demo lesson'}
                                        >
                                          {isUpdatingDemo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Presentation className="w-3.5 h-3.5" />{isDemo ? 'Demo' : 'Mark demo'}</>}
                                        </Button>
                                      );
                                    })()}
                                  </>
                                )}
                              </>
                            );
                          })()}
                          <Button
                            type="button"
                            size="sm"
                            className="gap-2 h-9 shrink-0 whitespace-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenChapter(chapter);
                            }}
                          >
                            <Play className="w-4 h-4" />
                            Open Lesson
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5 h-9 text-muted-foreground shrink-0 whitespace-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenChapter(chapter);
                            }}
                            title="Edit lesson content"
                          >
                            <Edit className="w-3.5 h-3.5" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
      
      {/* Loading overlay - clear layer, no overlap with content */}
      {/*
        A quiet line, not a curtain.

        This was a full overlay shown whenever `loading` was true and there were
        rows, so pressing Load more greyed out and blocked everything already
        read while the next page arrived. Appending should append; the only
        honest signal is that more is on the way.
      */}
      {loading && chapters.length > 0 && (
        <div
          className="flex items-center justify-center gap-2 border-t border-border py-3 text-xs text-muted-foreground"
          aria-live="polite"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading more chapters…
        </div>
      )}

      {/* Empty state - theme tokens */}
      {!loading && groups.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="p-4 rounded-xl bg-muted border border-border mb-4">
            <BookOpen className="w-12 h-12 text-muted-foreground" />
          </div>
          <p className="text-foreground text-sm font-medium">No chapters found</p>
          <p className="text-muted-foreground text-xs mt-1">Try adjusting your filters</p>
        </div>
      )}
    </div>
  );
};
